/** Interactive Microsoft sign-in for the plain-browser case. Inside Teams, teams-js `getAuthToken()` is used instead; both mint an Entra token for the SAME audience, so the backend has one validation path.
 *  MSAL is deliberately not used inside Teams (the tab is already an iframe, the nested-frame situation `getAuthToken()` exists to avoid).
 *  Entra setup: a Single-page application platform (not Web) with this origin as a redirect URI; authorization code + PKCE, so no client secret in the browser. */
import {
  BrowserAuthError,
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser'

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ''
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? ''
const authorityOverride = import.meta.env.VITE_ENTRA_AUTHORITY_TENANT ?? ''

/** The scope requested for this API: the `access_as_user` scope on the Application ID URI. `User.Read` alone would return a Graph token, which this API rightly rejects. */
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? ''

export const isMsalConfigured = Boolean(clientId && tenantId && apiScope)

/** Which specific variables are missing, so the UI can say, not just refuse. */
export function missingMsalConfig(): string[] {
  return [
    !clientId && 'VITE_ENTRA_CLIENT_ID',
    !tenantId && 'VITE_ENTRA_TENANT_ID',
    !apiScope && 'VITE_ENTRA_API_SCOPE',
  ].filter((entry): entry is string => Boolean(entry))
}

/** MSAL's own default. The same wait governs the daily iframe renewal once the 24-hour SPA refresh token has expired, so a shorter one signs people out. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export const SILENT_RENEWAL_TIMEOUT_MS = 10_000
const SIGNED_IN_HINT_KEY = 'trg.msal.signedIn'
const RENEW_BOUNCE_KEY = 'trg.msal.renewBounce'

/** Pure, exported for msal.test.ts. The hidden-iframe `ssoSilent` is tried only for a browser that has signed in on this origin before: for a first-time visitor it waits out the iframe timeout and delays the sign-in gate for nothing. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export function shouldTrySilentSso(hasSignedInBefore: boolean, coolingDown: boolean): boolean {
  return hasSignedInBefore && !coolingDown
}

function hasSignedInBefore(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN_HINT_KEY) === '1'
  } catch {
    return false
  }
}

function setSignedInHint(on: boolean): void {
  try {
    if (on) localStorage.setItem(SIGNED_IN_HINT_KEY, '1')
    else localStorage.removeItem(SIGNED_IN_HINT_KEY)
  } catch {
    // Storage blocked: the hint is only an optimisation.
  }
}

/** Pure, exported for msal.test.ts. Only `organizations` overrides the tenant: a single-tenant registration refuses the shared endpoints, so this flips after the portal step, never before. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export function msalAuthority(tenant: string, override: string): string {
  const segment = override.trim() === 'organizations' ? 'organizations' : tenant
  return `https://login.microsoftonline.com/${segment}`
}

const configuration: Configuration = {
  auth: {
    clientId,
    authority: msalAuthority(tenantId, authorityOverride),
    redirectUri: window.location.origin,
  },
  cache: {
    // localStorage, not sessionStorage: someone who signed in yesterday lands straight in the app today, and every new tab would otherwise fall back to the sign-in button.
    // The cached account outlives the tab on a shared machine, which is why "Sign out" clears it rather than only dropping the local copy.
    cacheLocation: 'localStorage',
  },
  system: {
    iframeBridgeTimeout: SILENT_RENEWAL_TIMEOUT_MS,
  },
}

let instance: PublicClientApplication | null = null
let initialised: Promise<void> | null = null

function getInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication(configuration)
  }
  return instance
}

/** Must complete before any other MSAL call: `handleRedirectPromise()` consumes the auth code after sign-in, and without it the redirect loops. */
export async function initMsal(): Promise<void> {
  if (!isMsalConfigured) return
  if (!initialised) {
    initialised = (async () => {
      const msal = getInstance()
      await msal.initialize()
      let result = null
      try {
        result = await msal.handleRedirectPromise()
      } catch (error) {
        // The prompt=none bounce answers an error when Microsoft's own session is gone: expected, so only a real sign-in's error is reported.
        if (takeBounceInFlight()) {
          console.debug('[msal] the silent sign-in redirect came back without a token', error)
        } else {
          console.error('[msal] Microsoft sign-in did not complete', error)
          redirectError = error instanceof Error ? error.message : String(error)
        }
      }
      if (result?.account) {
        msal.setActiveAccount(result.account)
        setSignedInHint(true)
      } else if (!msal.getActiveAccount()) {
        // Adopt a lone cached account (the returning user lands straight in), but with SEVERAL adopt nothing and let the gate ask:
        // picking `getAllAccounts()[0]` is a coin toss deciding whose data renders, and `ssoFromExistingSession` already declines the same question.
        const accounts = msal.getAllAccounts()
        if (accounts.length === 1) {
          msal.setActiveAccount(accounts[0])
          setSignedInHint(true)
        }
      }
    })()
  }
  return initialised
}

export function getActiveAccount(): AccountInfo | null {
  if (!isMsalConfigured) return null
  return getInstance().getActiveAccount()
}

/** How many Microsoft accounts this browser has cached, active or not. `getActiveAccount()` cannot answer this: with several cached accounts `initMsal()` leaves the active one unset.
 *  Reads the cache synchronously, so call it after `initMsal()` has resolved. */
export function cachedAccountCount(): number {
  if (!isMsalConfigured) return 0
  return getInstance().getAllAccounts().length
}

/** Drop every Microsoft account cached in this browser, and say whether that worked.
 *  ⛔ `clearCache()`, not `setActiveAccount(null)` alone: the row would stay in `getAllAccounts()` and `initMsal()` would adopt it again on the next boot.
 *  Local only: no redirect and no round trip to Entra, so the person mid-sign-in is not navigated away and their Microsoft session elsewhere is untouched.
 *  ⚠ Returns false if any account survived; the caller must not treat the browser as clean then. */
export async function forgetCachedAccounts(): Promise<boolean> {
  if (!isMsalConfigured) return true
  await initMsal()
  const msal = getInstance()
  setSignedInHint(false)
  try {
    // Explicit, rather than trusting the active pointer to be among the keys `clearCache()` wipes.
    msal.setActiveAccount(null)
    await msal.clearCache()
  } catch (error) {
    console.error('[msal] could not clear the cached account', error)
  }
  return msal.getAllAccounts().length === 0
}

/** Open Microsoft's sign-in page in a popup; resolves once the active account is set. A blocked popup falls back to a redirect, which never returns. */
export async function signIn(): Promise<void> {
  if (!isMsalConfigured) {
    throw new Error(
      `Microsoft sign-in is not configured. Missing: ${missingMsalConfig().join(', ')}`,
    )
  }
  await initMsal()
  const msal = getInstance()

  // Full-page redirect, not a popup: the popup's cross-window handshake is fragile behind a tunnel/reverse proxy (BrowserAuthError: timed_out). `initMsal()`'s `handleRedirectPromise()` finishes the sign-in on the next boot.
  // `select_account` always shows the chooser: without it Entra signs in as whoever it saw last. Reaching here means the user asked to sign in, so the extra click is the point.
  await msal.loginRedirect({ scopes: [apiScope], prompt: 'select_account' })
}

/** Sign out of this app only: drop the cached account, never the Microsoft session (a provider logout would sign the person out of Outlook and Teams web too). `signIn()`'s `select_account` keeps the next sign-in from silently reusing that session. */
export async function signOut(): Promise<void> {
  if (!(await forgetCachedAccounts())) {
    console.error('[msal] sign-out left a cached account behind')
  }
}

/** Sign out of this app AND the Microsoft session (Outlook and Teams web too), then land back on the origin's sign-in page. Navigates away, so callers end any other session first. */
export async function signOutOfMicrosoft(): Promise<void> {
  if (!isMsalConfigured) return
  await initMsal()
  const msal = getInstance()
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
  if (!(await forgetCachedAccounts())) {
    console.error('[msal] Microsoft sign-out left a cached account behind')
  }
  try {
    await msal.logoutRedirect({
      logoutHint: account?.username,
      postLogoutRedirectUri: window.location.origin,
    })
  } catch (error) {
    console.error('[msal] Microsoft sign-out redirect failed; the local accounts are already cleared', error)
  }
}

/** Last resort before the sign-in button: redeem a live Entra session cookie without UI, via a hidden iframe with `prompt=none`.
 *  Refusal is normal (no session, or several with no hint as to which): return null quietly and fall through to the gate. Browsers blocking third-party cookies can also fail here. */
async function ssoFromExistingSession(): Promise<string | null> {
  const msal = getInstance()
  try {
    const result = await msal.ssoSilent({ scopes: [apiScope] })
    if (result.account) {
      msal.setActiveAccount(result.account)
      setSignedInHint(true)
    }
    return result.accessToken
  } catch {
    silentFailure = { at: Date.now(), needsInteraction: true, timedOut: false, retried: false }
    return null
  }
}

/** The last silent failure, or null. Module scope on purpose: a real sign-in is a full-page redirect, so it resets by itself.
 *  A failed attempt can take ~10s (hidden iframe `timed_out`), so for a short window after one the next call answers null at once instead of doubling the splash. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export type SilentFailure = { at: number; needsInteraction: boolean; timedOut: boolean; retried: boolean }
let silentFailure: SilentFailure | null = null
const SILENT_RETRY_COOLDOWN_MS = 30_000

/** Pure, exported for msal.test.ts. */
export function silentCoolingDown(failedAt: number, now: number): boolean {
  return failedAt > 0 && now - failedAt < SILENT_RETRY_COOLDOWN_MS
}

/** Pure, exported for msal.test.ts. Inside the cooldown, a `fresh` call (the retry after a 401) still gets ONE more try after a transient failure; a failure Microsoft says needs the user is not retried. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export function silentAttemptAllowed(failure: SilentFailure | null, now: number, fresh: boolean): boolean {
  if (!failure || !silentCoolingDown(failure.at, now)) return true
  return fresh && !failure.needsInteraction && !failure.retried
}

/** Pure, exported for msal.test.ts. A top-level bounce redeems the first-party Entra cookie that a blocked or timed-out iframe cannot, but only for the cached account, never over a code session or while offline, and once per tab. @public msal.test.ts reads it from a dynamic import after its mocks, which knip cannot follow. */
export function shouldRenewByRedirect(state: {
  account: boolean
  codeSession: boolean
  failure: Pick<SilentFailure, 'needsInteraction' | 'timedOut'> | null
  online: boolean
  bouncedThisTab: boolean
}): boolean {
  const needsMicrosoft = Boolean(state.failure?.needsInteraction || (state.failure?.timedOut && state.online))
  return state.account && !state.codeSession && needsMicrosoft && !state.bouncedThisTab
}

function bounceState(): string | null {
  try {
    return sessionStorage.getItem(RENEW_BOUNCE_KEY)
  } catch {
    return 'blocked'
  }
}

function setBounce(value: 'in-flight' | 'done' | null): void {
  try {
    if (value) sessionStorage.setItem(RENEW_BOUNCE_KEY, value)
    else sessionStorage.removeItem(RENEW_BOUNCE_KEY)
  } catch {
    // Storage blocked: `bounceState()` then reads non-null, so no bounce is attempted.
  }
}

function takeBounceInFlight(): boolean {
  if (bounceState() !== 'in-flight') return false
  setBounce('done')
  return true
}

/** Why the last interactive Microsoft sign-in came back without signing in, once, for the sign-in screen to say. */
let redirectError: string | null = null

export function takeRedirectError(): string | null {
  const message = redirectError
  redirectError = null
  return message
}

/** Renew a cached Microsoft account whose silent renewal needs Microsoft, by a `prompt=none` redirect: no typing, and Microsoft answers an error rather than a page when its session is gone.
 *  Resolves true when the page is navigating away. ⛔ Only the sign-in gate calls this; `acquireApiToken` stays silent. */
export async function renewSignInByRedirect(codeSession: boolean): Promise<boolean> {
  if (!isMsalConfigured) return false
  await initMsal()
  const msal = getInstance()
  const account = msal.getActiveAccount()
  const go = shouldRenewByRedirect({
    account: account !== null,
    codeSession,
    failure: silentFailure,
    online: typeof navigator === 'undefined' || navigator.onLine !== false,
    bouncedThisTab: bounceState() !== null,
  })
  if (!go || !account) return false
  setBounce('in-flight')
  try {
    await msal.loginRedirect({ scopes: [apiScope], loginHint: account.username, prompt: 'none' })
    return true
  } catch (error) {
    console.error('[msal] could not start the silent sign-in redirect', error)
    return false
  }
}

/** A token for this API, or null if the user is not signed in. Silent, always: every failure returns null and the caller decides what to do; this never navigates the page.
 *  ⛔ Do not re-add escalation to a popup/redirect (pinned in msal.test.ts): `getToken()` calls this on every request, so it fired on page load and sent a valid email-code session to Microsoft. Interactive sign-in has one owner, `signIn()`.
 *  ⛔ `allowSilentSso: false` turns off the `ssoSilent` fallback, which redeems whatever Entra cookie the browser holds and could hand back the LAST Microsoft user's token; `./teams` passes false whenever a code session is live. */
export async function acquireApiToken(
  options: { allowSilentSso?: boolean; fresh?: boolean } = {},
): Promise<string | null> {
  const { allowSilentSso = true, fresh = false } = options
  if (!isMsalConfigured) return null
  await initMsal()

  if (!silentAttemptAllowed(silentFailure, Date.now(), fresh)) return null
  const retrying = silentFailure !== null && silentCoolingDown(silentFailure.at, Date.now())

  const msal = getInstance()
  const account = msal.getActiveAccount()
  if (!account) {
    return allowSilentSso && shouldTrySilentSso(hasSignedInBefore(), false) ? await ssoFromExistingSession() : null
  }

  try {
    const result = await msal.acquireTokenSilent({ scopes: [apiScope], account, forceRefresh: fresh })
    setSignedInHint(true)
    silentFailure = null
    setBounce(null)
    return result.accessToken
  } catch (error) {
    const needsInteraction = error instanceof InteractionRequiredAuthError
    const timedOut = error instanceof BrowserAuthError && error.errorCode === 'timed_out'
    silentFailure = { at: Date.now(), needsInteraction, timedOut, retried: retrying }
    if (needsInteraction) {
      console.debug('[msal] silent renewal needs the user; deferring to the sign-in gate')
      return null
    }
    console.error('[msal] silent token acquisition failed', error)
    return null
  }
}
